import { ReactNode } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import cn from 'clsx'
import * as routerActions from 'connected-react-router'

// Actions
import { actions as browseActions } from 'modules/browse/actions'

// Components
import { IconCloseFat, useMaxWidth, MultiSelectDropdown, Button } from '@tracklib/kit'
import Header from 'components/filter/filters/header'
import SliderDropdown from 'components/filter/filters/sliderdropdown'
import SortMode from 'components/browse/sortmode'

// Hooks
import { useSearchConfig, useCurrentQuery } from 'modules/browse/hooks'

// Types
import { QueryObject, Genre, Region, TrackType, State } from 'types'

// Utils
import { OnlyLarge } from 'components/filter/filters/label/label'
import { handleSliderUpdate, handleListItemUpdate, handleClearSpecificFilter } from 'components/filter/filters/utils'
import { getFilters } from 'components/filter/filters/info'

import styles from './filters.css'

type FiltersProps = {
  selectedFilters: Array<string>
  sortModes: Array<{ value: number; name: string }>
  toggleMobileFilterVisible: (state: boolean) => void
  queryObj?: QueryObject
}

const Filters = ({ selectedFilters, sortModes, toggleMobileFilterVisible, queryObj }: FiltersProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const searchConfig = useSearchConfig(queryObj)
  const currentQuery = useCurrentQuery()
  const isSmall = useMaxWidth()
  const { ordering, ...currentQueryWithoutOrder } = currentQuery
  const activeFilters = Object.keys(currentQueryWithoutOrder)
  // if multitrack and types is active remove 1 from count as multitrack is nested inside types
  const activeFiltersLength =
    activeFilters.includes('types') && activeFilters.includes('multitrack') ? activeFilters.length - 1 : activeFilters.length
  const hasActiveFilters = !!activeFiltersLength

  const trackTypeList = useSelector<State, Array<TrackType>>(state => Object.values(state.entities.trackTypes))
  const trackTypeOptions = trackTypeList.filter(x => !x.parent)
  const genreOptions = useSelector<State, Array<Genre>>(state => Object.values(state.entities.genres))
  const regionList = useSelector<State, Array<Region>>(state => Object.values(state.entities.regions))
  const regionOptions = regionList.filter(x => !x.parent)

  const allAvailableFilters = getFilters(currentQuery, searchConfig, genreOptions, regionOptions, trackTypeOptions, queryObj)
  const filters = selectedFilters.map(filterKey => allAvailableFilters[filterKey as keyof typeof allAvailableFilters])
  const fewFilters = filters.length < 4

  const updateSearchWithQuery = (qObj: QueryObject) => dispatch(browseActions.updateSearchWithQuery(qObj))
  return (
    <div className={styles.wrapper}>
      <Header
        activeFilters={activeFiltersLength}
        onClear={() => dispatch(routerActions.push({}))}
        toggleMobileFilterVisible={toggleMobileFilterVisible}
      />
      <div className={cn(styles.dropdowns, fewFilters && styles.dropdownsFew)}>
        {sortModes.length > 0 && <SortMode sortModes={sortModes} className={styles.mobileSortMode} />}
        {filters.map(filter => {
          const {
            type,
            id,
            label,
            options,
            selected,
            datatesting,
            min,
            max,
            values,
            labelSeparation,
            header,
            listClassName,
            selectedOptionsCount,
          } = filter as {
            type?: 'slider'
            id: string
            label: ReactNode
            options: Array<{ id: number; name: string }>
            selected: Array<number>
            datatesting: string
            min: number
            max: number
            values: [number, number]
            labelSeparation: number
            header: ReactNode
            listClassName: string
            selectedOptionsCount: number
          }
          const isSlider = type === 'slider'
          if ((!isSlider && !options?.length) || (isSlider && !isNaN(min) && min === max)) {
            return null
          }

          const sharedProps = {
            key: id,
            label,
            selectedOptionsClassName: styles.selectedOptions,
            closesOnBackdropClick: !isSmall,
            listClassName: cn(styles.list, listClassName),
            datatesting,
            onClear: handleClearSpecificFilter(id, currentQuery, updateSearchWithQuery),
            className: styles.dropdown,
            labelClassName: styles.label,
          }
          if (isSlider) {
            return (
              <SliderDropdown
                {...sharedProps}
                onChange={handleSliderUpdate(id, currentQuery, searchConfig, updateSearchWithQuery)}
                min={min}
                max={max}
                values={values}
                labelSeparation={labelSeparation} // end slider-only options
                listClassName={cn(styles.list, listClassName)}
                selectedOptionsClassName={styles.selectedOptions}
                closesOnBackdropClick={!isSmall}
                datatesting={datatesting}
              />
            )
          }
          return (
            <MultiSelectDropdown
              {...sharedProps}
              onChange={handleListItemUpdate(id, currentQuery, updateSearchWithQuery)}
              options={options}
              selected={selected}
              header={header}
              selectedOptionsCount={selectedOptionsCount} // end list-only options
              listWrapperClassName={styles.listWrapper}
              itemClassName={styles.item}
              checkboxClassName={styles.checkbox}
            />
          )
        })}
      </div>
      <div className={styles.buttons}>
        <Button
          query={{ ...(ordering ? { ordering } : {}) }}
          variant="outline"
          onClick={() => toggleMobileFilterVisible(false)}
          className={cn(styles.clearSearch, hasActiveFilters && styles.clearSearchVisible)}
          datatesting="clear_search"
          disabled={!hasActiveFilters}
        >
          <IconCloseFat /> Clear <OnlyLarge reversed>filters</OnlyLarge>
        </Button>
        <Button className={styles.showResults} onClick={() => toggleMobileFilterVisible(false)}>
          Show results
        </Button>
      </div>
    </div>
  )
}

export default Filters
